import { useState } from "react";
import { ImageOff, Package, Pencil, Plus, Trash2 } from "lucide-react";
import type { Product, Store } from "../../lib/api";
import { formatMoney } from "../../lib/money";
import { useDeleteProduct, useProducts } from "../../lib/store-api";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Dialog } from "../../components/ui/Dialog";
import { EmptyState } from "../../components/ui/EmptyState";
import { Spinner } from "../../components/ui/Spinner";
import { ProductEditor } from "./ProductEditor";

function stockLabel(product: Product): { text: string; variant: "default" | "warning" | "error" } {
  if (product.stock === null) return { text: "Not tracked", variant: "default" };
  if (product.stock <= 0) return { text: "Sold out", variant: "error" };
  if (product.stock <= 5) return { text: `${product.stock} left`, variant: "warning" };
  return { text: `${product.stock} in stock`, variant: "default" };
}

export function ProductsTab({ siteId, store }: { siteId: string; store: Store }) {
  const { data: products, isPending, error } = useProducts(siteId);
  const deleteProduct = useDeleteProduct(siteId);
  const [editing, setEditing] = useState<Product | "new" | null>(null);
  const [deleting, setDeleting] = useState<Product | null>(null);

  const collections = Array.from(new Set((products ?? []).map((p) => p.collection).filter(Boolean))).sort();

  return (
    <section aria-label="Products">
      <div className="store-toolbar">
        <p className="text-muted">
          {products ? `${products.length} ${products.length === 1 ? "product" : "products"}` : " "}
        </p>
        <Button onClick={() => setEditing("new")}>
          <Plus size={16} aria-hidden="true" />
          Add product
        </Button>
      </div>

      {isPending ? (
        <div className="loading-center" style={{ padding: "var(--z-space-12)" }}>
          <Spinner />
        </div>
      ) : null}
      {error ? (
        <div className="form-error" role="alert">
          Could not load products.
        </div>
      ) : null}

      {products && products.length === 0 ? (
        <EmptyState
          icon={<Package size={28} />}
          title="No products yet"
          description="Add your first product with photos, a price and options like size or colour."
          action={
            <Button onClick={() => setEditing("new")}>
              <Plus size={16} aria-hidden="true" />
              Add product
            </Button>
          }
        />
      ) : null}

      {products && products.length > 0 ? (
        <ul className="product-list">
          {products.map((product) => {
            const stock = stockLabel(product);
            const image = product.images[0];
            return (
              <li key={product.id} className="product-row">
                <div className="product-row__thumb">
                  {image ? <img src={image.url} alt="" loading="lazy" /> : <ImageOff size={18} aria-hidden="true" />}
                </div>
                <div className="product-row__main">
                  <strong>{product.name}</strong>
                  <span className="text-muted text-sm">
                    {product.collection || "No collection"}
                    {product.options.length > 0
                      ? ` · ${product.options.map((o) => `${o.values.length} ${o.name.toLowerCase()}s`).join(", ")}`
                      : ""}
                  </span>
                </div>
                <div className="product-row__price">
                  {formatMoney(product.priceMinor, store.currency)}
                  {product.compareAtMinor ? (
                    <s className="text-muted text-sm">{formatMoney(product.compareAtMinor, store.currency)}</s>
                  ) : null}
                </div>
                <div className="product-row__badges">
                  <Badge variant={stock.variant}>{stock.text}</Badge>
                  {product.status === "draft" ? <Badge>Draft</Badge> : null}
                </div>
                <div className="product-row__actions">
                  <button
                    type="button"
                    className="icon-btn"
                    aria-label={`Edit ${product.name}`}
                    onClick={() => setEditing(product)}
                  >
                    <Pencil size={16} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="icon-btn icon-btn--danger"
                    aria-label={`Delete ${product.name}`}
                    onClick={() => setDeleting(product)}
                  >
                    <Trash2 size={16} aria-hidden="true" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}

      {editing ? (
        <ProductEditor
          siteId={siteId}
          currency={store.currency}
          product={editing === "new" ? null : editing}
          collections={collections}
          onClose={() => setEditing(null)}
        />
      ) : null}

      <Dialog
        open={Boolean(deleting)}
        title="Delete product"
        onClose={() => setDeleting(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={deleteProduct.isPending}
              onClick={() => {
                if (deleting) deleteProduct.mutate(deleting.id, { onSuccess: () => setDeleting(null) });
              }}
            >
              Delete product
            </Button>
          </>
        }
      >
        <p>
          Delete <strong>{deleting?.name}</strong>? It disappears from your store straight away. Past orders keep
          their copy.
        </p>
      </Dialog>
    </section>
  );
}
